/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace product
 */

/* global theme, YT */
import {load, register} from '@shopify/theme-sections';
import {ProductForm , getUrlWithVariant} from '@shopify/theme-product-form';
import {formatMoney} from '@shopify/theme-currency';
import Swiper, { Navigation, Thumbs, Pagination } from 'swiper';
import Drift from 'drift-zoom';

const localeUrl = window.localeUrl || '';

// Style includes
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/thumbs';

const selectors = {
    addToCart: '[data-add-to-cart]',
    addToCartText: '[data-add-to-cart-text]',
    comparePrice: '[data-compare-price]',
    comparePriceText: '[data-compare-text]',
    originalSelectorId: '[data-product-select]',
    priceWrapper: '[data-price-wrapper]',
    productImageWrapper: '[data-product-image-wrapper]',
    productFeaturedImage: '[data-product-featured-image]',
    productJson: '[data-product-page]',
    productPrice: '[data-product-price]',
    productThumbnailsSlider: 'carousel--product-thumbnails',
    productFeatureSlider: 'carousel--featured-product',
    productSlides: '.swiper-wrapper .swiper-slide',
    productThumbs: '[data-product-single-thumbnail]',
    productForm: '[data-product-form]',
    radioOptionSelector: '[data-radio-option-selector]',
    singleOptionSelector: '[data-single-option-selector]',
    productMediaWrapper: '[data-product-single-media-wrapper]',
    productMediaTypeVideo: '[data-product-media-type-video]',
    productMediaTypeModel: '[data-product-media-type-model]',
    variantOptionLabels: '.swatch__option label',
    formIncrement: '.form__increment',
    quantity: '#Quantity',
    productSwiperThumbs: null,
    productSwiperFeature: null,
    notifyCartButton: '.button__notify-restock'
};

const cssClasses = {
    activeThumbnail: 'active-thumbnail',
    hide: 'hide',
};

document.addEventListener('DOMContentLoaded', () => {
    /**
     * Product section constructor. Runs on page load as well as Theme Editor
     * `section:load` events.
     * @param {string} container - selector for the section container DOM element
    */
    register('product', {
        async onLoad() {
            const productFormElement = document.querySelector(selectors.productForm);
            const productHandle      = productFormElement.dataset.productHandle;
            const selectedVariantId  = productFormElement.dataset.onloadVariantId;

            // Stop parsing if we don't have the product json script tag when loading
            // section in the Theme Editor
            if (!this.container.querySelector(selectors.productJson)?.innerHTML) {
                return;
            }

            await fetch(`${Shopify.routes.root}products/${productHandle}.js`)
                .then(response => {
                    return response.json();
                })

                .then(productJSON => {
                    this.product = productJSON;
                    this.productForm = new ProductForm(productFormElement, this.product, {
                        onOptionChange: this.onFormOptionChange.bind(this),
                        onQuantityChange: this.onFormQuantityChange.bind(this)
                    });
                });

            this.container.addEventListener('chooseNewSwatch', this.onForcedSwatchChange.bind(this));

            this.currentVariant = this.productForm.product.variants.filter(variant => variant.id == selectedVariantId)[0];
            this.updateMobileStickyPrice(this.currentVariant);
            this.featuredImage = document.querySelector(selectors.productFeaturedImage);
            this.swiperSlides = this.featuredImage.querySelectorAll(selectors.productSlides);

            // this.initProductCarousel();

            const breakpoint = window.matchMedia( '(min-width:1024px)' );

            const checkBreakpoint = function(element) {
                if ( breakpoint.matches === true ) {
                    if ( selectors.productSwiperThumbs !== null ) {
                        selectors.productSwiperThumbs.destroy( true, true );
                    }
                    if ( selectors.productSwiperFeature !== null ) {
                        selectors.productSwiperFeature.destroy( true, true );
                    }

                    element.container.classList.add('product--grid');
                    element.container.classList.add('product-images--show');
                    return;
                } else if ( breakpoint.matches === false ) {
                    element.container.classList.remove('product--grid');
                    element.container.classList.add('product-images--show');
                    return element.initProductCarousel();
                }
            };
            breakpoint.addEventListener('change', () => checkBreakpoint(this));
            checkBreakpoint(this);

            this.initProductVideo();
            this.initVariantTitles();
            this.initVariantHoverWatch();
            this.updatePickupAvailability(this.currentVariant);
            this.initStickyCart();
            this.initCrossSells();
            this.initProductZoom();
            this.initRebuyBundleIntegration();
            this.container
                .querySelectorAll('.form__field--radio-width input[data-single-option-selector]')
                .forEach(input => {
                    input.addEventListener('change', this.onForcedSwatchChange.bind(this));
                });
        },

        onFormOptionChange(event) {
            const currentVariant = {variant: event.dataset.variant, target: this.container};
            if (currentVariant.variant) {
                this.currentVariant = currentVariant.variant;
            }

            this.updatePDPInformation(currentVariant);
            this.updateProductPrices(currentVariant);
            this.updateURLwithVariant(currentVariant);

            if (this.featuredImage && this.swiperSlides.length > 1) {
                this.updateImages(currentVariant);
            }
        },

        // toggleWideIndicator(isWide) {
        //     const indicators = this.container.querySelectorAll('.form__option-label--size .wide-indicator');
        //     indicators.forEach(span => {
        //         span.style.display = isWide ? 'inline' : 'none';
        //     });
        //     },

        onForcedSwatchChange() {
            const swatchOptions = [...document.querySelectorAll('.swatch__option')];

            // Find all inputs (radio or select) with data-single-option-selector that are checked/selected
            const selectedOptions = [];

            // Get all selected radio inputs inside swatch__option
            swatchOptions.forEach(optionEl => {
                const radio = optionEl.querySelector('input[type="radio"]:checked');
                if (radio) {
                    // data-index format: "option1", "option2", etc.
                    const optionIndex = parseInt(radio.dataset.index.replace('option', '')) - 1;
                    selectedOptions[optionIndex] = radio.value;
                }
            });

            // Also get selected <select> inputs with data-single-option-selector (if any)
            const selectInputs = [...document.querySelectorAll('select[data-single-option-selector]')];
            selectInputs.forEach(select => {
                const optionIndex = parseInt(select.dataset.index.replace('option', '')) - 1;
                selectedOptions[optionIndex] = select.value;
            });

            // If some options are undefined, fill with empty string (optional)
            for (let i = 0; i < this.productForm.product.options.length; i++) {
                if (typeof selectedOptions[i] === 'undefined') selectedOptions[i] = '';
            }

            // Find variant that matches all selected options exactly
            const matchedVariant = this.productForm.product.variants.find(variant => {
                return variant.options.every((optValue, i) => optValue === selectedOptions[i]);
            });

            if (matchedVariant) {
                this.currentVariant = matchedVariant;
            } else {
                // fallback: select first available variant
                this.currentVariant = this.productForm.product.variants[0];
            }

            const currentVariantEvent = { variant: this.currentVariant, target: this.container };

            this.updatePDPInformation(currentVariantEvent);
            this.updateProductPrices(currentVariantEvent);
            this.updateURLwithVariant(currentVariantEvent);

            if (this.featuredImage && this.swiperSlides.length > 1) {
                this.updateImages(currentVariantEvent);
            }

            const widthInput = document.querySelector('input[data-index="option2"]:checked'); // Adjust if width is not option2
            const isWide = widthInput && widthInput.value.toLowerCase().includes('wide');

            document.querySelectorAll('.wide-indicator').forEach(indicator => {
                indicator.style.display = isWide ? 'inline' : 'none';
            });
        },

        onFormQuantityChange(event) {
            const currentVariant = {variant: event.dataset.variant, target: this.container};
            this.updateProductPrices(currentVariant);
        },

        initVariantTitles() {
            this.changeVariantTitles(this.currentVariant);
        },

        updateURLwithVariant(event) {
            const variant = event.variant;

            if (!variant) return;

            const url = getUrlWithVariant(window.location.href, variant.id);
            window.history.replaceState({ path: url }, '', url);
        },

        updatePDPInformation(event) {
            this.updateMasterSelect(event);
            this.updateAddToCartState(event);
            this.changeVariantTitles(event);
            this.updatePickupAvailability(event?.variant);
        },

        updateImages(evt) {
            const variant = evt.variant;

            if (typeof variant.featured_media != 'undefined') {

                const imageId = this.id + '-' + variant.featured_media.id;

                this.swapImage(imageId);
            }
        },

        updatePickupAvailability(variant) {
            const oldContainer = document.querySelector('[data-store-availability-container]');
            if (!oldContainer) {
                return;
            }
            oldContainer.innerHTML = '';
            fetch(window.Shopify.routes.root + `variants/${variant?.id}/?section_id=pickup-availability`)
                .then(response => response.text())
                .then(text => {
                    const container = document.querySelector('[data-store-availability-container]');
                    const pickupAvailabilityHTML = new DOMParser()
                        .parseFromString(text, 'text/html')
                        .querySelector('.shopify-section');

                    container.appendChild(pickupAvailabilityHTML);
                    const newHeight = container.offsetHeight;
                    const accordionContentContainer = document.querySelector('#accordion--availability--content');
                    if (accordionContentContainer.classList.contains('is-open')) {
                        accordionContentContainer.style.height = `${newHeight}px`;
                    }
                })
                .catch(e => {
                    console.error(e);
                });

        },

        updateMasterSelect(event) {
            const variant = event.variant;

            if (!variant) return;

            this.container.querySelector(selectors.originalSelectorId).value = variant.id;
        },

        updateAddToCartState(event) {
            const variant = event.variant;
            const addToCartButtons = this.container.querySelectorAll(selectors.addToCart);
            const notifyCartButtons = this.container.querySelectorAll(selectors.notifyCartButton);
            const productForm = this.container.querySelector(selectors.productForm);
            const hasBIS = productForm?.dataset.hasBis === 'true';

            if (variant) {
                this.container.querySelector(selectors.priceWrapper).classList.remove(cssClasses.hide);

                // Hide the BIS button only if BIS is enabled
                if (hasBIS) {
                    notifyCartButtons.forEach((button) => {
                        button.classList.add(cssClasses.hide);
                    });
                }
            } else {
                addToCartButtons.forEach((button) => {
                    button.disabled = true;
                    button.querySelector(selectors.addToCartText).innerHTML = theme.strings.unavailable;
                    button.classList.add(cssClasses.hide);
                });
                this.container.querySelector(selectors.priceWrapper).classList.add(cssClasses.hide);

                // Display the BIS button only if BIS is enabled
                if (hasBIS) {
                    notifyCartButtons.forEach((button) => {
                        button.classList.remove(cssClasses.hide);
                        button.classList.remove('button--disabled');
                    });
                }

                return;
            }

            if (variant.available) {
                addToCartButtons.forEach((button) => {
                    button.disabled = false;
                    button.classList.remove(cssClasses.hide);
                    button.querySelector(selectors.addToCartText).innerHTML = theme.strings.addToCart;
                });

                // Hide the BIS button only if BIS is enabled
                if (hasBIS) {
                    notifyCartButtons.forEach((button) => {
                        button.classList.add(cssClasses.hide);
                        button.classList.add('button--disabled');
                    });
                }
            } else {
                addToCartButtons.forEach((button) => {
                    button.classList.add(cssClasses.hide);
                    button.disabled = true;
                    button.querySelector(selectors.addToCartText).innerHTML = theme.strings.soldOut;
                });

                // Display the BIS button only if BIS is enabled
                if (hasBIS) {
                    notifyCartButtons.forEach((button) => {
                        button.classList.remove(cssClasses.hide);
                        button.classList.remove('button--disabled');
                    });
                }
            }
        },

        changeVariantTitles() {
            document.querySelectorAll('.swatch__option input[data-single-option-selector]:checked').forEach((input) => {
                const swatchOptionLabel = input.closest('.swatch__option')?.querySelector('.form__option-label:not(.form__option-label--hidden)');
                const container = input.closest('.form__element');
                const optionId = container?.id;

                if (!swatchOptionLabel || !optionId) return;

                // Now safely find the subtitle
                const titleEl = document.querySelector(`.swatch__title[data-swatch-option="${optionId}"]`);
                const subtitle = titleEl?.querySelector('.swatch__subtitle');

                if (subtitle) {
                    const labelText = swatchOptionLabel.dataset.label ?? swatchOptionLabel.textContent;
                    subtitle.textContent = labelText;
                }

                // Sticky values update (safely)
                const stickyValue = document.querySelector('.sticky__option-value');
                const mobileStickyValue = document.querySelector('.product-sticky--mobile .sticky__option-value');

                if (stickyValue) {
                    stickyValue.textContent = swatchOptionLabel.dataset.label;
                }

                if (mobileStickyValue) {
                    mobileStickyValue.textContent = swatchOptionLabel.dataset.label;
                }
            });

            const skuElement = document.querySelector('[data-product-sku]');
            const variantSelect = document.querySelector('select[data-product-select]');

            if (skuElement && variantSelect) {
                const selectedOption = variantSelect.options[variantSelect.selectedIndex];
                const selectedVariantSku = selectedOption.dataset.sku;

                if (selectedVariantSku && selectedVariantSku !== 'null') {
                    skuElement.textContent = selectedVariantSku;
                } else {
                    skuElement.textContent = ''; // or remove the whole `.product__sku` if needed
                }
            }
        },

        tempChangeVariantTitle(event) {
            const target = event.currentTarget;
            const newLabel = target.dataset.label ?? target.textContent;

            // Find the closest .form__element
            const container = target.closest('.form__element');

            // Get the ID of the form__element (e.g., "option1")
            const optionId = container?.id;
            if (!optionId) return;

            // Now find the corresponding subtitle using that ID
            const titleElement = document.querySelector(`.swatch__title[data-swatch-option="${optionId}"]`);
            const subtitle = titleElement?.querySelector('.swatch__subtitle');

            if (subtitle) {
                subtitle.textContent = newLabel;
            } else {
                console.warn(`No subtitle found for option ID: ${optionId}`);
            }
        },

        initVariantHoverWatch() {
            const changeVariantTitles = this.changeVariantTitles;
            const tempChangeVariantTitle = this.tempChangeVariantTitle;
            document.querySelectorAll('.swatch__option input[data-single-option-selector] ~ label.form__option-label').forEach((swatch__option) => {
                swatch__option.addEventListener('mouseenter', tempChangeVariantTitle);
                swatch__option.addEventListener('mouseleave', changeVariantTitles);
            });
        },

        updateProductPrices(evt) {
            const variant = evt.variant;
            if (!variant) {
                return false;
            }
            const comparePrice = this.container.querySelector(selectors.comparePrice);
            const comparePriceText = this.container.querySelector(selectors.comparePriceText);
            const quantity = parseInt(this.container.querySelector(selectors.quantity).value);

            this.container.querySelector(selectors.productPrice).innerHTML = formatMoney(variant.price, theme.settingCurrencyFormat);
            this.updateMobileStickyPrice(variant);

            // Calculate total including Rebuy items if present
            const priceWithQuantity = variant.price * quantity;
            let totalPrice = priceWithQuantity;

            // Check if Rebuy widget exists and has selections
            const rebuyWidget = this.container.querySelector('[data-rebuy-id]');
            if (rebuyWidget && window.Rebuy?.Cart?.cart?.items) {
                const rebuyItems = window.Rebuy.Cart.cart.items;
                const rebuyTotal = rebuyItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                totalPrice = priceWithQuantity + rebuyTotal;
            }

            document.querySelector('#addToCartPricing').innerHTML = formatMoney(totalPrice, theme.moneyWithoutCurrency);

            if (variant.compare_at_price > variant.price) {
                comparePrice.innerHTML = formatMoney(variant.compare_at_price, theme.settingCurrencyFormat);
                comparePrice.classList.remove(cssClasses.hide);
                comparePriceText.classList.remove(cssClasses.hide);
                this.container.querySelector(selectors.productPrice).classList.add('price__original--sale');
            } else if(comparePrice) {
                comparePrice.innerHTML = '';
                comparePrice.classList.add(cssClasses.hide);
                comparePriceText.classList.add(cssClasses.hide);
                this.container.querySelector(selectors.productPrice).classList.remove('price__original--sale');
            }
        },

        disableVariants(target, valuesToEnable) {
            // Loop through all the Option Selectors and check which one is a swatch selector
            // The target is usually the main product container
            const optionSelector1 = target.querySelectorAll('[data-index="option1"]');
            const optionSelector2 = target.querySelectorAll('[data-index="option2"]');
            const optionSelector3 = target.querySelectorAll('[data-index="option3"]');
            let is_swatch_selector1 = false;
            let is_swatch_selector2 = false;
            let is_swatch_selector3 = false;

            if (optionSelector1.length > 0 && optionSelector1[0].getAttribute('type') == 'radio') {
                is_swatch_selector1 = true;
            }

            if (optionSelector2.length && optionSelector2[0].getAttribute('type') == 'radio') {
                is_swatch_selector2 = true;
            }

            if (optionSelector3.length && optionSelector3[0].getAttribute('type') == 'radio') {
                is_swatch_selector3 = true;
            }

            const option1 = target.querySelectorAll('input[data-index="option1"]');
            const option2 = target.querySelectorAll('input[data-index="option2"]');
            const option3 = target.querySelectorAll('input[data-index="option3"]');

            // If there are 3 option selectors
            if (optionSelector3.length > 0) {

                // If the third option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1 and 2.
                if (is_swatch_selector3 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option3.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option3.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the second option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1 and 3.
                } else if (is_swatch_selector2 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option2.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the first option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 2 and 3.
                } else if (is_swatch_selector1 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option1.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });
                }

            // If there are 2 option selectors
            } else if (optionSelector2.length > 0) {

                // If the second option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1.
                if (is_swatch_selector2 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option2.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the first option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 2.
                } else if (is_swatch_selector1 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option1.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });
                }
            } else {
                if (is_swatch_selector1 == true) {
                    option1.forEach((option) => {
                        const input = option;

                        const labels = input.parentNode.querySelectorAll('label');

                        labels.forEach(label => {
                            label.classList.remove('swatch--disabled');
                        });
                        if (!valuesToEnable.option1.includes(input.value)) {
                            labels.forEach(label => {
                                label.classList.add('swatch--disabled');
                            });
                        }
                    });
                }
            }
        },

        initStickyCart() {
            const options = {
                rootMargin: '100px 0px 0px 0px',
                threshold: [0,0.01,1]
            };
            const target = document.querySelector('#productStickyContainer');
            const observer = new IntersectionObserver(this.showHideStickyCart, options);

            observer.observe(target);
        },

        showHideStickyCart(entries) {
            let entry = entries[0];
            const mobileStickyBar = document.querySelector('.product-sticky--mobile');

            if (entry.intersectionRatio >= 1) {
                entry.target.classList.remove('product__sticky--show');
                entry.target.classList.add('product__sticky--hide');
                if (mobileStickyBar) {
                    mobileStickyBar.classList.remove('product-sticky--mobile--show');
                    mobileStickyBar.setAttribute('aria-hidden', 'true');
                }
            } else if (entry.intersectionRatio <= 0 && entry.boundingClientRect.y <= 0) {
                entry.target.classList.add('product__sticky--show');
                entry.target.classList.remove('product__sticky--hide');
                if (mobileStickyBar) {
                    mobileStickyBar.classList.add('product-sticky--mobile--show');
                    mobileStickyBar.setAttribute('aria-hidden', 'false');
                }
            } else {
                entry.target.classList.add('product__sticky--hide');
                entry.target.classList.remove('product__sticky--show');
                if (mobileStickyBar) {
                    mobileStickyBar.classList.remove('product-sticky--mobile--show');
                    mobileStickyBar.setAttribute('aria-hidden', 'true');
                }
            }
        },

        updateMobileStickyPrice(variant) {
            const mobilePrice = document.querySelector('.product-sticky--mobile__price');
            if (mobilePrice && variant) {
                mobilePrice.textContent = formatMoney(variant.price, theme.moneyWithoutCurrency);
            }
        },

        initCrossSells() {
            const kitContainer = document.querySelector('.product__complete-kit');
            if (!kitContainer) return;

            const url = kitContainer.dataset.completeKitUrl;
            if (!url) return;

            fetch(localeUrl + url.replace(window.location.origin, ''))
                .then(response => response.json())
                .then(data => {
                    const products = data.products;
                    if (!products || products.length === 0) return;

                    const titleEl = document.createElement('h3');
                    titleEl.className = 'complete-kit__title';
                    titleEl.textContent = 'Effortless Cutting Starts Here.';

                    const productsEl = document.createElement('div');
                    productsEl.className = 'complete-kit__products';

                    kitContainer.setAttribute('data-loaded', '');

                    products.forEach(product => {
                        const variant = product.variants.find(v => v.available) || product.variants[0];
                        if (!variant) return;

                        const price = formatMoney(variant.price, theme.moneyWithoutCurrency);
                        const imageSrc = product.featured_image
                            ? product.featured_image.replace(/(\.\w+)$/, '_medium$1')
                            : '';

                        const card = document.createElement('div');
                        card.className = 'complete-kit__card';
                        card.dataset.variantId = variant.id;

                        card.innerHTML = `
                            <button class="complete-kit__select-btn" type="button" aria-pressed="false"
                                data-variant-id="${variant.id}" data-product-title="${product.title}">
                                <div class="complete-kit__card-left">
                                    <div class="complete-kit__card-image-wrapper">
                                        ${imageSrc ? `<img class="complete-kit__card-image" src="${imageSrc}" alt="${product.title}" loading="lazy">` : ''}
                                    </div>
                                    <div class="complete-kit__card-info">
                                        <span class="complete-kit__card-name">${product.title}</span>
                                        <span class="complete-kit__card-price">${price}</span>
                                    </div>
                                </div>
                                <span class="complete-kit__add-icon" aria-hidden="true">+</span>
                            </button>
                        `;

                        card.querySelector('.complete-kit__select-btn').addEventListener('click', function() {
                            const isSelected = this.getAttribute('aria-pressed') === 'true';
                            this.setAttribute('aria-pressed', isSelected ? 'false' : 'true');
                        });

                        productsEl.appendChild(card);
                    });

                    kitContainer.appendChild(titleEl);
                    kitContainer.appendChild(productsEl);
                })
                .catch(e => console.error('Cross-sell load error:', e));

            const mobileStickyAtcBtn = document.querySelector('[data-mobile-sticky-atc]');
            if (mobileStickyAtcBtn) {
                mobileStickyAtcBtn.addEventListener('click', () => {
                    const mainQty = document.querySelector('#Quantity');
                    const stickyQty = document.querySelector('[data-mobile-sticky-quantity]');
                    if (mainQty && stickyQty) mainQty.value = stickyQty.value;
                    const mainAtcBtn = document.querySelector('[data-add-to-cart]');
                    if (mainAtcBtn) mainAtcBtn.click();
                });
            }
        },

        initProductVideo: function() {
            const sectionId = this.id;
            const videoContainers = document.querySelectorAll(selectors.productMediaTypeVideo, this.container);

            videoContainers.forEach((videoContainer) => {
                ProductVideo.init(videoContainer, sectionId);
            });
        },

        initProductZoom: function() {
            if (matchMedia('(pointer:fine)').matches && window.innerWidth> 1023) {
                const driftImgs = document.querySelectorAll('.js--image-zoom');

                driftImgs.forEach((image) => {
                    let container = image.closest('.swiper-slide');

                    // Documentation link for reference: https://github.com/imgix/drift#basic-usage
                    new Drift(image, {
                        containInline: true,
                        inlineContainer: container,
                        inlineOffsetX: 0,
                        inlineOffsetY: 0,
                        inlinePane: true,
                        handleTouch: false,
                        injectBaseStyles: true,
                        hoverDelay: 200,
                    });
                });
            }
        },

        initProductCarousel() {
            // -------------------------------------
            // Carousel Products
            // -------------------------------------

            const productFeatureSwiper = new Swiper('.'+selectors.productFeatureSlider+'', {
                // Optional parameters
                modules: [Navigation, Thumbs, Pagination],
                spaceBetween: 0,

                // // Navigation arrows
                navigation: {
                    nextEl: '.swiper-arrow__container--next',
                    prevEl: '.swiper-arrow__container--prev',
                },
                pagination: {
                    el: '.swiper-pagination',
                    type: 'fraction',
                },
                on: {
                    init: function() {
                        toggleInnerFocus(this);
                    }
                }
            });

            // The "on" event is proper to the Swiper JS library.
            productFeatureSwiper.on('slideChange ', function () {
                // If current slide has video, trigger "mediaVisible" event to play the video. Then if the previous slide has video, trigger the "mediaHidden" event to pause it.
                const previousSlideIndex = productFeatureSwiper.previousIndex,
                    currentSlideIndex = productFeatureSwiper.activeIndex;

                // If current slide has video, trigger "mediaVisible" event to play the video. Then if the previous slide has video, trigger the "mediaHidden" event to pause it.
                const currentMedia = document.querySelector('.product__feature-image .swiper-slide[data-index="'+currentSlideIndex+'"] .product-single__media-wrapper'),
                    previousMedia = document.querySelector('.product__feature-image .swiper-slide[data-index="'+previousSlideIndex+'"] .product-single__media-wrapper');

                previousMedia.dispatchEvent(new Event('mediaHidden'));
                currentMedia.dispatchEvent(new Event('mediaVisible'));
                toggleInnerFocus(this);
            });

            selectors.productSwiperFeature = productFeatureSwiper;
        },

        slideToSlide(slider, slideIndex, transition) {
            if (transition) {
                slider.slideTo(slideIndex);
            } else {
                slider.slideTo(slideIndex, 0);
            }
        },

        // triggering change image when using with Slider
        swapImage(imageId) {
            let newImageId = imageId;

            // If "imageId" is not defined in the function parameter, find it by the current product image
            if (typeof newImageId === 'undefined') {
                newImageId = document.querySelector(
                    `${selectors.productImageWrapper}:not('.${cssClasses.hide}')`
                ).dataset.imageId;
            }

            const thumbnail = document.querySelector(
                `${selectors.productThumbs}[data-thumbnail-id='${newImageId}']`
            );
            const slideIndex = parseInt(thumbnail.parentElement.getAttribute('data-index'));

            // Swipe JS
            this.slideToSlide(selectors.productSwiperThumbs, slideIndex, false);
            this.slideToSlide(selectors.productSwiperFeature, slideIndex, false);
        },

        /**
         * Initialize Rebuy bundle integration
         * Ensures main product is added along with Rebuy selections
         * and updates pricing dynamically
         */
        initRebuyBundleIntegration() {

            const addToCartButton = this.container.querySelector(selectors.addToCart);
            const rebuyWidget = this.container.querySelector('[data-rebuy-id]');
            const addToCartPricing = document.querySelector('#addToCartPricing');


            if (!rebuyWidget || !addToCartButton) {
                return;
            }

            let addonsWidget = null;
            const WIDGET_ID = "274312"; // Your Rebuy widget ID

            const notifyCartUpdated = () => {
                document.dispatchEvent(new CustomEvent('cart:updated'));
            };

            // Get base price from DOM
            const getBasePrice = () => {
                // Try to get from currentVariant first
                if (this.currentVariant?.price) {
                    return this.currentVariant.price;
                }

                // Fallback: parse from the price display element
                const priceElement = this.container.querySelector('[data-product-price]');
                if (priceElement) {
                    const priceText = priceElement.textContent.trim();
                    // Remove currency symbols and parse
                    const priceMatch = priceText.match(/[\d,]+\.?\d*/);
                    if (priceMatch) {
                        const dollars = parseFloat(priceMatch[0].replace(/,/g, ''));
                        return Math.round(dollars * 100); // Convert to cents
                    }
                }
                return 0;
            };

            // Function to calculate and update the total price
            const updateTotalPrice = () => {
                const quantity = parseInt(this.container.querySelector(selectors.quantity)?.value || 1);
                const basePrice = getBasePrice();
                let total = basePrice * quantity;

                const subtotalEl = document.querySelector('.rebuy-addon__subtotal-value .rebuy-money span:not(.sr-only)');

                if (subtotalEl) {
                    const dollars = parseFloat(subtotalEl.textContent.trim().replace(/[^0-9.]/g, ''));
                    if (!isNaN(dollars) && dollars > 0) {
                        total += Math.round(dollars * 100);
                    }
                }

                if (addToCartPricing) {
                    addToCartPricing.innerHTML = formatMoney(total, theme.moneyWithoutCurrency);
                }
            };

            // Document-level change listener as fallback in case rebuyWidget doesn't contain the checkboxes
            document.addEventListener('change', (e) => {
                if (e.target.classList.contains('rebuy-checkbox')) {
                    setTimeout(updateTotalPrice, 100);
                }
            });

            // Listen for Rebuy widget ready event
            document.addEventListener('rebuy.ready', (event) => {
                const { widget } = event.detail;
                if (String(widget.id) === WIDGET_ID) {
                    addonsWidget = widget;
                    updateTotalPrice();
                }
            });

            // Try to get widget directly from window.Rebuy
            const tryDirectCapture = () => {
                if (window.Rebuy && window.Rebuy.widgets) {
                    const widget = window.Rebuy.widgets.find(w => String(w.id) === WIDGET_ID);
                    if (widget) {
                        addonsWidget = widget;
                        updateTotalPrice();
                    }
                }
            };

            setTimeout(tryDirectCapture, 1000);
            setTimeout(tryDirectCapture, 2000);

            const setRebuyButtonLabel = (button, label) => {
                if (!button) {
                    return;
                }

                button.classList.toggle('is-added', label === 'Added');
                button.dataset.rebuyState = label === 'Added' ? 'added' : 'default';
                button.setAttribute('aria-label', label);
                button.setAttribute('title', label);

                if (button.tagName === 'INPUT') {
                    button.value = label;
                }

                if (button.dataset.buttonText !== undefined) {
                    button.dataset.buttonText = label;
                }
                if (button.dataset.addToCartText !== undefined) {
                    button.dataset.addToCartText = label;
                }

                const textTargets = button.querySelectorAll('[data-add-to-cart-text], .rebuy-button-text, span');
                if (textTargets.length) {
                    textTargets.forEach((target) => {
                        target.textContent = label;
                    });
                } else if (button.firstChild && button.firstChild.nodeType === Node.TEXT_NODE) {
                    button.firstChild.textContent = label;
                } else {
                    const newLabelNode = document.createElement('span');
                    newLabelNode.textContent = label;
                    button.appendChild(newLabelNode);
                }

                const currentHtml = button.innerHTML;
                button.innerHTML = currentHtml
                    .replace(/Add\s*to\s*Cart/gi, label)
                    .replace(/Added/gi, label);
            };

            const getHandleFromHref = (href) => {
                if (!href) {
                    return null;
                }

                const match = href.match(/\/products\/([^/?#]+)/);
                return match ? match[1] : null;
            };

            const collectBlockIdentifiers = (block) => {
                const productIds = new Set();
                const variantIds = new Set();
                const handles = new Set();

                const classList = Array.from(block.classList);
                classList.forEach((className) => {
                    if (className.includes('product-id-')) {
                        productIds.add(className.replace('product-id-', ''));
                    }
                    if (className.includes('variant-id-')) {
                        variantIds.add(className.replace('variant-id-', ''));
                    }
                });

                const addIfValue = (set, value) => {
                    if (value !== null && typeof value !== 'undefined' && String(value).trim() !== '') {
                        set.add(String(value));
                    }
                };

                addIfValue(productIds, block.dataset.productId);
                addIfValue(variantIds, block.dataset.variantId);
                addIfValue(handles, block.dataset.productHandle || block.dataset.handle);

                block.querySelectorAll('[data-product-id], [data-variant-id], [data-product-handle], [data-handle]').forEach((node) => {
                    addIfValue(productIds, node.dataset.productId);
                    addIfValue(variantIds, node.dataset.variantId);
                    addIfValue(handles, node.dataset.productHandle || node.dataset.handle);
                });

                const rebuyButton = block.querySelector('.rebuy-button');
                if (rebuyButton) {
                    addIfValue(productIds, rebuyButton.dataset.productId);
                    addIfValue(variantIds, rebuyButton.dataset.variantId);
                    addIfValue(handles, rebuyButton.dataset.productHandle || rebuyButton.dataset.handle);
                }

                const productLinks = block.querySelectorAll('.rebuy-product-title-link, a[href*="/products/"]');
                productLinks.forEach((link) => {
                    const handle = getHandleFromHref(link.getAttribute('href'));
                    addIfValue(handles, handle);
                });

                return {
                    productIds,
                    variantIds,
                    handles
                };
            };

            // Function to keep added products visible and update button text
            const syncRebuyButtonState = (cart) => {
                const productIdsInCart = new Set();
                const variantIdsInCart = new Set();
                const productHandlesInCart = new Set();

                cart.items.forEach((item) => {
                    if (item.product_id) {
                        productIdsInCart.add(String(item.product_id));
                    }
                    if (item.id) {
                        variantIdsInCart.add(String(item.id));
                    }
                    if (item.handle) {
                        productHandlesInCart.add(String(item.handle));
                    }
                });

                rebuyWidget.querySelectorAll('.rebuy-product-block').forEach(block => {
                    const button = block.querySelector('.rebuy-button');
                    if (!button) {
                        return;
                    }

                    const identifiers = collectBlockIdentifiers(block);
                    const isInCartByProduct = Array.from(identifiers.productIds).some((productId) => productIdsInCart.has(String(productId)));
                    const isInCartByVariant = Array.from(identifiers.variantIds).some((variantId) => variantIdsInCart.has(String(variantId)));
                    const isInCartByHandle = Array.from(identifiers.handles).some((handle) => productHandlesInCart.has(String(handle)));

                    const isInCart = isInCartByProduct || isInCartByVariant || isInCartByHandle || block.classList.contains('cart-has-item');

                    if (isInCart) {
                        setRebuyButtonLabel(button, 'Added');
                    } else {
                        setRebuyButtonLabel(button, 'Add to Cart');
                    }
                });
            };

            const refreshRebuyButtonState = (emitUpdate = false) => {
                fetch('/cart.js', {
                    cache: 'no-cache',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                })
                    .then(response => response.json())
                    .then((cart) => {
                        syncRebuyButtonState(cart);
                        if (emitUpdate) {
                            notifyCartUpdated();
                        }
                    })
                    .catch((error) => {
                        console.error('Failed to refresh Rebuy button state:', error);
                    });
            };

            let cartSyncTimeout = null;
            const scheduleCartSync = (emitUpdate = false, delay = 700) => {
                if (cartSyncTimeout) {
                    clearTimeout(cartSyncTimeout);
                }

                cartSyncTimeout = setTimeout(() => {
                    refreshRebuyButtonState(emitUpdate);
                }, delay);
            };

            const normalizeRebuySlides = () => {
                rebuyWidget.querySelectorAll('.splide .rebuy-product-block').forEach((block) => {
                    if (!block.classList.contains('splide__slide')) {
                        block.classList.add('splide__slide');
                    }
                });
            };

            // Listen for selection changes - use document-level event delegation
            // since Rebuy widgets are dynamically inserted
            document.addEventListener('click', (e) => {
                const isRebuyAddonClick =
                    e.target.classList.contains('rebuy-checkbox') ||
                    e.target.closest('.rebuy-checkbox') ||
                    e.target.closest('.rebuy-checkbox-label') ||
                    e.target.closest('.rebuy-addon__item');

                if (isRebuyAddonClick) {
                    // Wait for Rebuy to update its subtotal before reading it
                    setTimeout(updateTotalPrice, 300);
                }

                // Handle Rebuy button clicks - mark product as added after delay
                const rebuyButton = e.target.closest('.rebuy-button');
                if (rebuyButton && rebuyWidget.contains(rebuyButton)) {
                    const productBlock = rebuyButton.closest('.rebuy-product-block');
                    if (productBlock) {
                        setRebuyButtonLabel(rebuyButton, 'Added');
                        scheduleCartSync(true, 1200);
                    }
                }
            });

            document.addEventListener('cart:updated', () => {
                scheduleCartSync(false, 600);
            });

            // Update ATC price when a Rebuy checkbox is checked/unchecked
            rebuyWidget.addEventListener('change', (e) => {
                if (e.target.classList.contains('rebuy-checkbox')) {
                    setTimeout(updateTotalPrice, 50);
                }
            });

            const rebuyWidgetObserver = new MutationObserver(() => {
                normalizeRebuySlides();
                scheduleCartSync(false, 200);
            });

            rebuyWidgetObserver.observe(rebuyWidget, {
                childList: true,
                subtree: true
            });

            // Check for items already in cart on load
            setTimeout(() => {
                normalizeRebuySlides();
                refreshRebuyButtonState(false);
            }, 800);

            // Also update when quantity changes
            const quantityInput = this.container.querySelector(selectors.quantity);
            if (quantityInput) {
                quantityInput.addEventListener('change', updateTotalPrice);
            }

            // Initial update immediately to show base price
            updateTotalPrice();

            // Update again after delay when Rebuy is loaded
            setTimeout(() => {
                updateTotalPrice();
            }, 1500);
            // Override the form submit to add base product + Rebuy add-ons together
            productFormElement.addEventListener('submit', (e) => {
                const rebuyWidget = document.querySelector('[data-rebuy-id]');

                // Only intercept if Rebuy widget exists
                if (!rebuyWidget) {
                    return; // Let normal form submission happen
                }

                e.preventDefault();

                // Get the main product form data
                const formData = new FormData(productFormElement);
                const variantId = formData.get('id');
                const quantity = parseInt(formData.get('quantity') || 1);

                // Collect all items to add (main product + add-ons)
                const itemsToAdd = [
                    {
                        id: variantId,
                        quantity: quantity
                    }
                ];

                // Get Rebuy selected add-ons
                const rebuyCheckboxes = rebuyWidget.querySelectorAll('input[type="checkbox"]:checked');
                rebuyCheckboxes.forEach(checkbox => {
                    const rebuyProduct = checkbox.closest('[data-product-id]');
                    if (rebuyProduct) {
                        const productId = rebuyProduct.dataset.productId || rebuyProduct.dataset.variantId;
                        const productQty = rebuyProduct.querySelector('input[type="number"]')?.value || 1;

                        if (productId) {
                            itemsToAdd.push({
                                id: productId,
                                quantity: parseInt(productQty)
                            });
                        }
                    }
                });

                // Add all items to cart at once
                fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        items: itemsToAdd
                    })
                })
                .then(response => response.json())
                .then(data => {
                    console.log('Products added to cart:', data);

                    // Show cart modal if your theme has one
                    const cartModal = document.querySelector('[data-modal-target="cart_confirmation"]');
                    if (cartModal) {
                        // Trigger your theme's cart modal
                        document.dispatchEvent(new CustomEvent('cart:item-added'));
                    }

                    // Or show Rebuy SmartCart
                    if (window.Rebuy?.SmartCart) {
                        window.Rebuy.SmartCart.show();
                    }

                    // Trigger custom cart update event
                    notifyCartUpdated();
                })
                .catch(error => {
                    console.error('Error adding products to cart:', error);
                    // Show error message
                    const errorMsg = this.container.querySelector('.cart__message--error');
                    if (errorMsg) {
                        errorMsg.textContent = 'Error adding products to cart. Please try again.';
                    }
                });
            });
        },

        /**
         * Event callback for Theme Editor `section:unload` event
         */
        onUnload() {
            this.productForm.destroy();
        },

    });



    /**
     * Load Video content on Product Swiper
     */
    const ProductVideo = (() => {
        const videos = {};

        const hosts = {
            html5: 'html5',
            youtube: 'youtube'
        };

        const attributes = {
            enableVideoLooping: 'enable-video-looping',
            videoId: 'video-id'
        };

        // Initialize Video Container
        const init = (videoContainer, sectionId) => {
            if (!videoContainer) {
                return;
            }

            const videoElement = videoContainer.querySelectorAll('iframe, video')[0];
            const mediaId = videoContainer.dataset.mediaId;

            if (!videoElement) {
                return;
            }

            videos[mediaId] = {
                mediaId: mediaId,
                sectionId: sectionId,
                host: hostFromVideoElement(videoElement),
                container: videoContainer,
                element: videoElement,
            };

            const video = videos[mediaId];
            switch (video.host) {
                case hosts.html5:
                    setupPlyrVideos();
                    break;
                case hosts.youtube:
                    setupYouTubeVideos();
                    break;
            }
        };

        // Set Up HTML 5 Players
        const setupPlyrVideos = () => {
            loadVideos(hosts.html5);
        };

        // Set Youtube embedded Videos
        const setupYouTubeVideos = () => {
            window.YT.ready(function() {
                loadVideos(hosts.youtube);
            });
        };

        // Create Video Player
        const createPlayer = (video) => {
            if (video?.player) {
                return;
            }

            const productMediaWrapper = video.container.closest(
                selectors.productMediaWrapper
            );
            const enableLooping = productMediaWrapper.dataset[attributes.enableVideoLooping];

            switch (video.host) {
                case hosts.html5:
                    video.player = new Shopify.Plyr(video.element, {
                        loop: { active: enableLooping }
                    });
                    toggleInnerFocus(selectors.productSwiperFeature);
                    break;
                case hosts.youtube: {
                    const videoId = productMediaWrapper.dataset[attributes.videoId];
                    video.player = new YT.Player(video.element, {
                        videoId: videoId,
                        events: {
                            onStateChange: (event) => {
                                if (event.data === 0 && enableLooping) event.target.seekTo(0);
                            }
                        }
                    });
                    break;
                }
            }

            productMediaWrapper.addEventListener('mediaHidden', () => {
                if (!video.player) return;

                if (video.host === hosts.html5) {
                    video.player.pause();
                }

                if (video.host === hosts.youtube && video.player.pauseVideo) {
                    video.player.pauseVideo();
                }
            });

            productMediaWrapper.addEventListener('mediaVisible', () => {
                if (!video.player) return;

                if (video.host === hosts.html5) {
                    video.player.play();
                }

                if (video.host === hosts.youtube && video.player.playVideo) {
                    video.player.playVideo();
                }
            });
        };

        // Get host source from Video
        const hostFromVideoElement = (video) => {
            if (video.tagName === 'VIDEO') {
                return hosts.html5;
            }

            if (video.tagName === 'IFRAME') {
                if (
                    /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com|youtu\.?be)\/.+$/.test(
                        video.src
                    )
                ) {
                    return hosts.youtube;
                }
            }
            return null;
        };

        // Load Video Players
        const loadVideos = (host) => {
            for (const videoId in videos) {
                const video = Object.prototype.hasOwnProperty.call(videos, videoId) ? videos[videoId] : null;

                if (video?.host === host) createPlayer(video);
            }
        };

        // Remove Video Players
        const removeSectionVideos = (sectionId) => {
            for (const videoId in videos) {
                const video = Object.prototype.hasOwnProperty.call(videos, videoId) ? videos[videoId] : null;

                if (video?.sectionId === sectionId) {
                    if (video.player) video.player.destroy();
                    delete videos[videoId];
                }
            }
        };

        return {
            init: init,
            hosts: hosts,
            loadVideos: loadVideos,
            removeSectionVideos: removeSectionVideos
        };
    })();

    load('product');
});

/**
 * Slider Accessibility Helper
 */
const toggleInnerFocus = (slider) => {
    const focusableElements = ['iframe', 'input', 'select', 'option', 'textarea', 'button', 'video', 'label', 'a', '.plyr', 'model-viewer'];
    focusableElements.forEach((fosusableElement) => {
        slider?.slides.forEach((slide, index) => {
            if (index === slider.activeIndex) {
                slide.querySelectorAll(fosusableElement).forEach((elem) => {
                    elem?.removeAttribute('tabindex');
                });
            } else {
                slide.querySelectorAll(fosusableElement).forEach((elem) => {
                    if (elem === '.plyr') {
                        elem?.removeAttribute('tabindex');
                    }  else {
                        elem?.setAttribute('tabindex', '-1');
                    }
                });
            }
        });
    });
};
